import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { createAdminPatient } from '@/lib/api/admin';
import { PATIENT_TAG_CATALOG } from '@/lib/patient-tags';

function emptyForm() {
  return {
    displayName: '',
    firstName: '',
    lastName: '',
    sex: '',
    dob: '',
    phone: '',
    tags: [],
    localNotes: '',
  };
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

/**
 * Create a new (credential-less) patient. The backend assigns a synthetic loginId + random
 * password and stamps the caller's tenant. On success `onCreated(user)` lets the parent refresh.
 */
export function PatientCreateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setError(null);
    }
  }, [open]);

  const setField = (name) => (event) => {
    const value = event?.target?.value ?? event;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const toggleTag = (key) => {
    setForm((current) => {
      const has = current.tags.includes(key);
      return { ...current, tags: has ? current.tags.filter((t) => t !== key) : [...current.tags, key] };
    });
  };

  const isValid = useMemo(
    () => Boolean(form.displayName.trim() || form.firstName.trim() || form.lastName.trim()),
    [form],
  );

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!isValid || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const created = await createAdminPatient({
        displayName: form.displayName.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        sex: form.sex.trim(),
        dob: form.dob.trim() ? new Date(`${form.dob.trim()}T00:00:00.000Z`).toISOString() : '',
        phone: form.phone.trim(),
        tags: form.tags,
        localNotes: form.localNotes.trim(),
      });
      toast.success('Patient created.');
      onCreated?.(created);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || 'Could not create patient.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New patient</DialogTitle>
          <DialogDescription>
            Create a patient record. Patients don’t log in — no credentials are needed.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea viewportClassName="max-h-[60vh] px-0.5 py-1">
          <form className="space-y-4" onSubmit={handleSubmit}>
          <Field label="Display name">
            <Input value={form.displayName} onChange={setField('displayName')} placeholder="e.g. Jane Tan" autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <Input value={form.firstName} onChange={setField('firstName')} />
            </Field>
            <Field label="Last name">
              <Input value={form.lastName} onChange={setField('lastName')} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of birth">
              <Input type="date" value={form.dob} onChange={setField('dob')} />
            </Field>
            <Field label="Sex">
              <Input value={form.sex} onChange={setField('sex')} placeholder="M / F" />
            </Field>
          </div>
          <Field label="Phone (E.164)">
            <Input value={form.phone} onChange={setField('phone')} placeholder="+6591234567" className="font-mono text-sm" />
          </Field>

          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Tags</span>
            <div className="flex flex-wrap gap-1.5">
              {PATIENT_TAG_CATALOG.map((def) => {
                const active = form.tags.includes(def.key);
                const Icon = def.icon;
                return (
                  <button
                    key={def.key}
                    type="button"
                    onClick={() => toggleTag(def.key)}
                    title={def.description}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors',
                      active ? def.chipClassName : 'border-border bg-transparent text-muted-foreground hover:bg-muted',
                    )}
                  >
                    <Icon className={cn('h-3 w-3', active ? def.glyphClassName : '')} />
                    {def.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Notes (local — not synced to Plato)">
            <textarea
              value={form.localNotes}
              onChange={setField('localNotes')}
              rows={3}
              placeholder="Staff-only notes…"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          {error && <p className="text-sm text-destructive">{error}</p>}
          </form>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSaving}>
            {isSaving ? <Spinner className="mr-2 h-4 w-4" /> : null}
            Create patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PatientCreateDialog;
